import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Modal } from "../../common/Modal.jsx";
import { Button } from "../../bootstrap/index.jsx";
import { FaIcon } from "../../common/Icons.jsx";
import LoadingIndicator from '../../common/loading_indicator.jsx';
import AgGrid from '../../common/AgGrid'

import { project_report_get_status_tracker_data_path } from '~/routes';

const MODAL_ID = "status-tracker-modal";
const DEFAULT_ROW_HEIGHT = 42; // If row height isn't set, then this's the default value

export const StatusTrackerTable = ({ monitoringReportId, filter = {}, tableHeight = '400px', autoHeight = false }) => {
    const gridRef = useRef(null);
    const gridContainerRef = useRef(null);
    const [rowData, setRowData] = useState([]);
    const [filteredRowData, setFilteredRowData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [columnDefs] = useState([
        { headerName: 'Phase', field: 'monitoring_report_phase_name', rowGroup: true, flex: 1}, // Group by Phase
        { headerName: 'Reporting Round', field: 'reporting_round', pivot: true, resizable: true, marryChildren: true, flex: 1}, // Pivot on Reporting Round
        { headerName: 'Status',
          field: 'project_report_phase_status',
          pivot: true,
          resizable: true,
          flex: 1,
          pivotComparator: (a, b) => {
            // sort statuses
            const sortOrder = ['pending_fill', 'pending_resubmission', 'approved', 'rejected', 'assigned', 'queued'];
            return sortOrder.indexOf(a) - sortOrder.indexOf(b);
          },
        },
        { headerName: '', field: 'project_report_id', aggFunc: 'count', minWidth: 100, flex: 1},
    ]);


      const processPivotResultColGroupDef = (colDef) => {

        if (colDef.children && colDef.children.length > 0) {
          colDef.children.forEach((childColDef) => {
            // add class for status columns to render icons and get translations to statuses
            // other col defs for the status columns
            const status = childColDef.headerName;
            childColDef.headerClass = getStatusClass(status);
            childColDef.headerName = I18n.t(status);
            childColDef.suppressHeaderMenuButton = true;
            childColDef.suppressMovable = true;
            });

          } else {
            colDef.headerClass = (params) => {
              const status = params.displayName || params.colDef.headerName;
              return getStatusClass(status);
            };
          }

          colDef.openByDefault = true;
          return colDef;
      };

    const getStatusClass = (status) => {
      // get css class name based on the status string
        switch (status.toLowerCase()) {
          case 'approved':
            return 'header-approved';
          case 'rejected':
            return 'header-rejected';
          case 'pending_resubmission':
            return 'header-pending-submission';
          case 'pending_fill':
            return 'header-pending-submission';
          case 'assigned':
          case 'queued':
            return 'header-pending';
          default:
            return 'header-unknown'; // Default class for other statuses, there shouldn't be any though
        }
      };

    const measureTextWidth = (text) => {
        let canvas = document.createElement("canvas");
        let context = canvas.getContext("2d");
        context.font = "13px Roboto"; // ag-grid default font setting
        return context.measureText(text).width;
    }

    const setColumnWidth = (columnDefs) => {
        columnDefs.forEach(c => { c.width = measureTextWidth(c.headerName) + 60})
        return columnDefs
    }

    const onGridReady = (params) => {

      //gridRef.current.columnApi.autoSizeAllColumns();
    };
  
    // Load row data
    useEffect(() => {
        setLoading(true);
        const url = project_report_get_status_tracker_data_path({monitoring_report_id: monitoringReportId})
        jQuery.getJSON(
            url, 
            (data) => {
              data.rowData.forEach((rows) => {
                  // add 'Reporting round' text before the number of round
                  rows.reporting_round = `${I18n.t("reporting_round")} ${rows.reporting_round == null ? "0" : rows.reporting_round }`
                });
                setRowData(data.rowData);
                setFilteredRowData(data.rowData);
                setLoading(false);
            }
        )

    }, [monitoringReportId]);

    const isLastColumn = (cell) =>  {
      const lastIndex = cell.column.parent.displayedChildren.length - 1;
      return cell.column.colId === cell.column.parent.displayedChildren[lastIndex].colId;
  }

  const getRound = (cell) => {
    // give different round columns different classes
    const oddOrEven = cell.rowIndex % 2 == 0 ? 'even' : 'odd'
    const cellParentGroupId = cell.column.parent.groupId;
    if (cellParentGroupId.includes("round 1")) {
      return("round-1 " + oddOrEven)
    }
    else if(cellParentGroupId.includes("round 2")) {
      return("round-2 " + oddOrEven)
    }
    else if(cellParentGroupId.includes("round 3")) {
      return("round-3" + oddOrEven)
    }
    else if(cellParentGroupId.includes("round 4")) {
      return("round-4" + oddOrEven)
    }
    else if(cellParentGroupId.includes("round 5")) {
      return("round-5" + oddOrEven)
    }
    else if(cellParentGroupId.includes("round 6")) {
      return("round-6" + oddOrEven)
    }
    else {
      return("round-other " + oddOrEven)
    }
  }

  const defaultColumnDefs =  {
    cellStyle: (cell) => isLastColumn(cell) ? {'borderRight': '2px solid rgb(71, 169, 166)'} : null,
    cellClass: (cell) => getRound(cell)
  };

  useEffect(() => {
    if (_.isEmpty(filter)) return;

    // Remove empty values
    const activeFilters = Object.entries(filter).filter(([_, value]) => value);

    const filteredData = rowData.filter(item => {
      return Object.entries(activeFilters).every(([_, filterValue]) => {
        switch (filterValue[0]) {
          case 'phaseId':
            return item.monitoring_report_phase_id == filterValue[1];
          case 'round':
            return item.reporting_round == `${I18n.t('reporting_round')} ${filterValue[1]}`;
          case 'reviewerId':
            return item.assignee_ids?.includes(filterValue[1]);
          default:
            return true;
        }
      });
    });

    setFilteredRowData(filteredData);
  }, [filter]);

  const getRowHeight = useCallback((params) => {
    if (!autoHeight) return DEFAULT_ROW_HEIGHT;

    const totalRows = new Set(
      filteredRowData.map(item => item.monitoring_report_phase_name || "")
    ).size;

    if (!gridContainerRef.current || totalRows === 0) return DEFAULT_ROW_HEIGHT;

    const viewport = gridContainerRef.current.querySelector(".ag-body-viewport");
    if (!viewport) return DEFAULT_ROW_HEIGHT;
    const rowHeight = Math.floor(viewport.offsetHeight / totalRows)
    return Math.max(DEFAULT_ROW_HEIGHT, rowHeight);
  }, [filteredRowData]);

  const onGridSizeChanged = useCallback((params) => {
    if (!autoHeight) return;
    params.api.resetRowHeights();
  }, []);

    return (<>
    {
        loading ? <LoadingIndicator />
        :   <div className="grid-theme status-tracker-table" style={{ height: tableHeight}} ref={gridContainerRef}>
                <AgGrid
                    ref={gridRef}
                    pivotMode={true}
                    columnDefs={columnDefs}
                    defaultExcelExportParams = {{
                        sheetName: 'SmartME export',
                        author: 'SmartME'
                    }}
                    rowData={filteredRowData}
                    suppressAggFuncInHeader={true}
                    processPivotResultColGroupDef={processPivotResultColGroupDef}
                    autoGroupColumnDef={{
                      // handle the Group column definitions
                      headerName: I18n.t('review_phase'),
                      resizable: true,
                      suppressMovable: true,
                      cellClassRules: {
                        'group-cell-odd': params => params.node.rowIndex % 2 === 0,
                        'group-cell-even': params => params.node.rowIndex % 2 !== 0
                      },
                    }}
                    removePivotHeaderRowWhenSingleValueColumn= {true} // removes the empty 'project_report_id' column header from the grid
                    defaultColDef={defaultColumnDefs}
                    onGridReady={onGridReady}
                    getRowHeight={getRowHeight}
                    onGridSizeChanged={onGridSizeChanged}
                    >
                </AgGrid>
            </div>
    }
    </>)


}

export const StatusIconLegend = () => {
  // legend for explaining the icons
  return (
    <div className="status-tracker-legend">
      <div className='status-icon-label'>
        <FaIcon type='thumbs-up' style={{backgroundColor: "rgb(143, 223, 181)"}}/>
        <label>{I18n.t("approved")}</label>
      </div>
      <div className='status-icon-label'>
        <FaIcon type='thumbs-down' style={{backgroundColor: "rgb(246, 201, 197)"}}/>
        <label>{I18n.t("rejected")}</label>
      </div>
      <div className='status-icon-label'>
        <FaIcon type='clock' style={{backgroundColor: "rgb(202, 204, 211)"}}/>
        <label>{I18n.t("assigned_status") }</label>
      </div>
      <div className='status-icon-label'>
        <FaIcon type='clock' style={{backgroundColor: "rgb(165, 220, 243)"}}/>
        <label>{I18n.t("pending_resubmission")}</label>
      </div>
    </div>
  )
}

const SubmissionStatusTracker = ({monitoringReportId, className='ml-1'}) => {

    const [openModal, setOpenModal] = useState(false);

    return (
        <>
            <Button
                size="sm"
                type="default"
                className={className}
                onClick={() => setOpenModal(true)}
            ><FaIcon type="bar-chart" /> {I18n.t("status_tracker")}</Button>
            {
                openModal && 
                <Modal
                    modalId={MODAL_ID}
                    size="xl"
                    title={I18n.t("status_tracker")}
                    onClose={() => setOpenModal(false)}
                    hideButtons={true}
                    style={{minHeight: 100}}
                >
                    <StatusTrackerTable monitoringReportId={ monitoringReportId }/>
                    <StatusIconLegend/>
                </Modal>
            }

        </>
    )
}

export default SubmissionStatusTracker;